import { FormEvent, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { TFunction } from 'i18next'
import { Check } from 'baseui/icon'
import { useSnackbar } from 'baseui/snackbar'

import { selectWebsalesUsersManagementSelected } from '../WebsalesUsersManagementSelectors'
import { setSelectedWebsalesUser } from '../WebsalesUsersManagementSlice'
import { websalesUsersManagementActions } from '../WebsalesUsersManagementSagas'
import { SelectedWebsalesUser, WebsalesUserFormErrors } from '../types'

interface UseAddEditModalParams {
  setIsOpen: (open: boolean) => void
}

interface UseAddEditModalReturn {
  t: TFunction
  formErrors: WebsalesUserFormErrors
  selectedWebsalesUser: SelectedWebsalesUser
  isEdit: boolean
  updateSelectedWebsalesUser: (websalesUserInformation: Partial<SelectedWebsalesUser>) => void
  submitHandler: (event: FormEvent<HTMLFormElement>) => void
}

const useAddEditModal = ({ setIsOpen }: UseAddEditModalParams): UseAddEditModalReturn => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { enqueue } = useSnackbar()
  const [formErrors, setFormErrors] = useState<WebsalesUserFormErrors>({})
  const selectedWebsalesUser = useSelector(selectWebsalesUsersManagementSelected)
  const isEdit = !!selectedWebsalesUser.id

  const notifySuccess = (message?: string) => {
    setIsOpen(false)
    const fallback = isEdit
      ? t('websalesUsersManagement:success_update', 'Compte websales mis à jour')
      : t('websalesUsersManagement:success_create', 'Compte websales créé')
    enqueue({
      message: message || fallback,
      startEnhancer: ({ size }: { size: number }) => <Check size={size} />,
    })
  }

  const updateSelectedWebsalesUser = (websalesUserInformation: Partial<SelectedWebsalesUser>) => {
    dispatch(setSelectedWebsalesUser({ ...selectedWebsalesUser, ...websalesUserInformation }))
  }

  const submitHandler = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setFormErrors({})
    const { id = 0, username, password, is_active } = selectedWebsalesUser

    if (id) {
      dispatch({
        type: websalesUsersManagementActions.UPDATE_WEBSALES_USER,
        websalesUserData: { id, username, is_active },
        enqueue,
        onSuccess: notifySuccess,
        setFormErrors,
      })
      return
    }

    dispatch({
      type: websalesUsersManagementActions.CREATE_WEBSALES_USER,
      websalesUserData: { username, password },
      enqueue,
      onSuccess: notifySuccess,
      setFormErrors,
    })
  }

  return {
    t,
    formErrors,
    selectedWebsalesUser,
    isEdit,
    updateSelectedWebsalesUser,
    submitHandler,
  }
}

export default useAddEditModal
